/**
 * Pure decision helpers for the Staging v2 flow (behind the `catalog_staging` flag).
 *
 * v2 removes the up-front empty/stage picker: every non-skipped room is emptied,
 * and staging is triggered per-room in the review step. These helpers keep that
 * logic testable and out of the large view components.
 */

export type UploadGenerationMode = 'none' | 'empty-only';

/** v2 upload toggle: skipped rooms generate nothing, others get emptied. */
export function uploadModeForRoom(skipped: boolean): UploadGenerationMode {
  return skipped ? 'none' : 'empty-only';
}

/**
 * Resolves the effective generation mode for an image.
 * v2 default is always 'empty-only' (never 'both') — staging is chosen later.
 * Legacy keeps the historical default ('both' for stageable rooms).
 */
export function effectiveUploadMode(
  generationMode: string | undefined,
  v2Active: boolean,
  canStage: boolean,
): string {
  if (generationMode) return generationMode;
  if (v2Active) return 'empty-only';
  return canStage ? 'both' : 'empty-only';
}

/** Number of rooms that will be emptied in v2 (1 credit each) for the cost hint. */
export function emptyRoomCount(
  images: Array<{ generationMode?: string }>,
  v2Active: boolean,
  canStageFor: (i: { generationMode?: string }) => boolean,
): number {
  return images.filter(
    (i) => effectiveUploadMode(i.generationMode, v2Active, canStageFor(i)) !== 'none',
  ).length;
}

/**
 * v2 review: whether to render the big "Staging erstellen" card for a room.
 * The card appears from the start for every stageable, not-yet-staged room —
 * even while the empty is still generating. Whether it is *clickable* (empty
 * ready, not already staging) is a separate concern handled by the caller.
 */
export function showStagingCard(p: {
  v2Active: boolean;
  canStage: boolean;
  hasStaged: boolean;
}): boolean {
  return p.v2Active && p.canStage && !p.hasStaged;
}
